import styled, { css } from 'styled-components';

import { hexToRgbA } from 'utils/colors';

/** Shared rhythm for the 60px sidebar */
export const MENU_ITEM_SLOT = 54;
export const MENU_AVATAR_SIZE = 40;
/** Plus, grid, and disabled-group triggers */
export const MENU_ACTION_SIZE = MENU_AVATAR_SIZE;
export const MENU_ACTION_ICON_SIZE = 18;
export const MENU_ITEM_GAP = 4;
export const MENU_SECTION_GAP = 12;
/**
 * Room for the active-community pulse (18px box-shadow spread + scale).
 * Must stay in sync with `communityRipplePulse` in MainMenuItemWrapper.
 */
export const COMMUNITY_PULSE_BLEED = 24;

/** Lets pulse rings paint above/below the Perfect Scrollbar viewport. */
const communityPulseScrollClip = css`
  clip-path: inset(-${COMMUNITY_PULSE_BLEED}px 0 -${COMMUNITY_PULSE_BLEED}px 0);
  -webkit-clip-path: inset(
    -${COMMUNITY_PULSE_BLEED}px 0 -${COMMUNITY_PULSE_BLEED}px 0
  );
`;

export const menuActionCircle = css`
  width: ${MENU_ACTION_SIZE}px;
  height: ${MENU_ACTION_SIZE}px;
  min-width: ${MENU_ACTION_SIZE}px;
  min-height: ${MENU_ACTION_SIZE}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const itemSlot = css`
  width: ${MENU_ITEM_SLOT}px;
  height: ${MENU_ITEM_SLOT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const StyledMainMenuBlock = styled.div`
  min-width: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: ${MENU_SECTION_GAP}px 0;
  height: 100svh;
  max-height: 100svh;
  overflow: hidden;
  box-sizing: border-box;
  margin-right: ${({ isMenuOpen }) => (isMenuOpen ? 60 : 0)}px;
  transition: margin-right 0.3s;
  position: fixed;
  z-index: 100000000;
  background: ${({ theme }) => theme.color.blackSqueeze};
`;

export const StyledMainMenuTopBlock = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: start;
`;

export const StyledProfileSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

export const StyledCommunitiesSection = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: visible;

  & > *:not(:last-child) {
    margin-bottom: ${MENU_ITEM_GAP}px;
  }
`;

/** Pulls the scroll viewport so pulse can paint into adjacent sidebar slots. */
export const StyledCommunitiesListScroll = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: visible;
  margin-top: -${COMMUNITY_PULSE_BLEED}px;
  margin-bottom: 0;

  .communities-list-scrollbar.ps {
    height: 100%;
    ${communityPulseScrollClip}
  }
`;

export const StyledMainMenuCommunities = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: visible;
  padding: ${COMMUNITY_PULSE_BLEED}px 0;
  box-sizing: border-box;

  & > * {
    overflow: visible;
  }
`;

export const StyledMenuActionSlot = styled.div<{
  $height?: 'auto';
  $zIndex?: number;
}>`
  ${itemSlot}
  ${({ $height }) =>
    $height === 'auto' &&
    css`
      height: auto;
    `}
  ${({ $zIndex }) =>
    $zIndex &&
    css`
      z-index: ${$zIndex};
    `}
`;

/** Fills the 54px menu slot so sidebar menu triggers center like community rows. */
export const StyledTooltipTriggerWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const StyledSeparator = styled.div<{ $marginTop?: number }>`
  width: 40px;
  height: 1px;
  max-width: 40px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.color.casper};
  margin-horizontal: 0;
  margin-top: ${({ $marginTop }) => $marginTop ?? MENU_SECTION_GAP}px;
  margin-bottom: ${MENU_SECTION_GAP}px !important;
`;

export const StyledFaqButtonBlock = styled.div`
  ${itemSlot}
`;

export const StyledGrid3x3ButtonBlock = styled.div`
  ${menuActionCircle}
  background: ${({ theme }) => hexToRgbA(theme.color.pickledBluewood, 0.1)};
  color: ${({ theme }) => theme.color.pickledBluewood};
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${({ theme }) => hexToRgbA(theme.color.pickledBluewood, 0.16)};
  }

  &:active {
    transform: scale(0.96);
  }

  &.active {
    background: ${({ theme }) => theme.color.primary || '#007bff'};
    color: ${({ theme }) => theme.color.primaryForeground || '#fff'};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const StyledLogoSlot = styled.div`
  ${itemSlot}
  flex-shrink: 0;
`;

export const StyledLogoImage = styled.img`
  display: block;
  width: ${MENU_AVATAR_SIZE}px;
  height: ${MENU_AVATAR_SIZE}px;
  object-fit: contain;
  pointer-events: none;
`;

export const StyledBottomMenu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-bottom: ${MENU_ITEM_GAP}px;
`;
